/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Dropdown, Text } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { InlineEditForm } from "~/components/InlineEdit/InlineEditForm";
import { Loading } from "~/components/Utils/Loading";
import { AssignmentRuleFlat, EnvType } from "~/models";
import { displayEnvType } from "~/tools";

const BadgeWrap = styled.div`
  &:not(:first-child) {
    margin-left: 0.5rem;
  }
`;

export const environmentTypesOptions = [
  { label: "Production", value: EnvType.PRODUCTION },
  { label: "Development", value: EnvType.DEVELOPMENT },
  { label: "Staging", value: EnvType.STAGING },
  { label: "Other", value: EnvType.OTHER },
  { label: "Unknown", value: EnvType.UNKNOWN },
];

type Props = {
  item: AssignmentRuleFlat;
  onSubmit: (values: AssignmentRuleFlat) => Promise<void>;
  canEdit?: boolean;
};

function InlineDropdownAREnvTypes({ item, onSubmit, canEdit = true }: Props) {
  const { handleSubmit } = useForm();

  const [value, setValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const onEdit = () => {
    const environmentTypes = [...(item.environmentTypes || []), value];

    return onSubmit({ ...item, environmentTypes });
  };

  const onRemove = async (values: string) => {
    setIsLoading(true);
    const filterEnvs = item.environmentTypes.filter((env) => env !== values);
    await onSubmit({ ...item, environmentTypes: [...filterEnvs] });
    setIsLoading(false);
  };

  const filteredOptions = item.environmentTypes
    ? environmentTypesOptions?.filter(
        (e) => !item.environmentTypes?.includes(e.value)
      )
    : environmentTypesOptions;

  if (isLoading) {
    return <Loading />;
  }

  const DisplayText = (
    <Text as="div" m="0">
      <FlexContainer>
        {item?.environmentTypes?.map((env: string) => (
          <BadgeWrap key={env}>{displayEnvType(env)}</BadgeWrap>
        ))}
      </FlexContainer>
    </Text>
  );

  return (
    <InlineEditForm
      onSubmit={handleSubmit(onEdit)}
      display={DisplayText}
      canEdit={canEdit}
    >
      {item?.environmentTypes?.map((env: string) => (
        <BadgeWrap key={env}>{displayEnvType(env, onRemove)}</BadgeWrap>
      ))}

      <Dropdown
        minNumOfOptionsToShowSearchBox={6}
        css={{ width: "15rem", marginLeft: "0.5rem" }}
        onChange={(c: string) => setValue(c)}
        value={value}
        name="Select"
        options={filteredOptions || []}
      />
    </InlineEditForm>
  );
}

export { InlineDropdownAREnvTypes };
